// modules
import {
  OrderingServiceFetch,
  OrderingServiceFormatter,
} from 'modules/Ordering/services';
import OrdersTVService from 'modules/TradesPW3/Orders';
// constants
import ValidationService from 'modules/Validation';
import { edisService } from 'modules/Edis';
import LocaleService from 'modules/Localization';
import { broker } from 'modules/TradingChart/consts';
import LiveDataService from 'modules/LiveData';
import SearchService from 'modules/Search';
import { MarketFeederV2 } from 'modules/LiveData/typings';
import UserStore from 'modules/User';
import { CleverTapService } from 'modules/CleverTap';
import { EVENTS } from 'modules/Analytics/consts';
import { decomposeExchangeToken } from 'modules/BOD/modules/Search/utils';
import { getScripforEdis } from 'modules/Edis/util';
import {
  OrderType as TvOrderType,
  Side as TvSide,
  NotificationType,
 } from '../TradingChart/components/TradingView/sources/charting_library';
 import {
  PlaceOrderResult,
 } from '../TradingChart/components/TradingView/sources/charting_library.min';

class OrderingService {
  // TODO: after Demo need will do refactor
  placeOrder = async (
    preOrder: OrderWithCustomFields,
    underSurveillance = false,
  ) => {
    const data = OrderingServiceFormatter.generateCommonOrderRequestData(
      preOrder,
      underSurveillance,
    );
    const resp = await OrderingServiceFetch.placeOrderRequest(data);

    const orderStatus = resp?.data?.responseDetailList?.[0]?.status;
      if (orderStatus === 'R') {
        if (broker.instance) {
          broker.instance.showNotification(
            LocaleService.translate('PLACE_ORDER'),
            resp?.data?.responseDetailList?.[0]?.message || 'Order placement failed',
            NotificationType.Error,
        );
      }
      return Promise.reject(new Error('Order placement failed'));
    }

    const orderData = data?.orders[0];
    if (resp.success) {
      CleverTapService.requestPermission();
      const scripData = await SearchService.searchByExchangeToken(
        preOrder.symbol,
      );

      OrderingServiceFormatter.sendMixpanelEventForCommonOrderPlaced(
        'Placed',
        orderData,
        scripData,
        this.getInstantOrderEnabled(),
      );
      OrdersTVService.getOrders();

      return {
        orderId: resp?.data?.responseDetailList?.[0]?.upstoxId,
      };
    }

    if (broker.instance) {
      broker.instance.showNotification(
        LocaleService.translate('PLACE_ORDER'),
        resp.error.message,
        NotificationType.Error,
      );
    }

    return Promise.reject(new Error('Order placement failed'));
  };

  checkIfOrderIsValid = async (order: OrderWithCustomFields) => {
    const {
      limitPrice,
      seenPrice: ltp,
      stopPrice,
      type: orderType,
      side: tvSide,
      symbol,
    } = order;
    const scripData = await SearchService.searchByExchangeToken(symbol);
    const liveData = LiveDataService.getByInstrumentKey(
      scripData?.instrumentKey || '',
    ) as MarketFeederV2.FormattedFullFeed;
    const { upperCircuit, lowerCircuit } = liveData;

    if (orderType === TvOrderType.StopLimit && limitPrice && ltp && stopPrice) {
      if (tvSide === TvSide.Buy) {
        if (limitPrice < stopPrice) {
          throw new Error(
            LocaleService.translate(
              'BUY_STOP_LOSS_LIMIT_ORDER_ERROR_MESSAGE_1',
            ),
          );
        } else if (stopPrice < ltp) {
          throw new Error(
            LocaleService.translate(
              'BUY_STOP_LOSS_LIMIT_ORDER_ERROR_MESSAGE_2',
            ),
          );
        }
      }
      if (tvSide === TvSide.Sell) {
        if (limitPrice > stopPrice) {
          throw new Error(
            LocaleService.translate(
              'SELL_STOP_LOSS_LIMIT_ORDER_ERROR_MESSAGE_1',
            ),
          );
        } else if (stopPrice > ltp) {
          throw new Error(
            LocaleService.translate(
              'SELL_STOP_LOSS_LIMIT_ORDER_ERROR_MESSAGE_2',
            ),
          );
        }
      }
    }

    if (orderType === TvOrderType.StopLimit) {
      if (
        (Number(limitPrice) < Number(lowerCircuit) ||
          Number(limitPrice) > Number(upperCircuit)) &&
        (Number(stopPrice) < Number(lowerCircuit) ||
          Number(stopPrice) > Number(upperCircuit))
      ) {
        throw new Error(
          LocaleService.translate(
            'CIRCUIT_LIMIT_ERROR',
            lowerCircuit || '',
            upperCircuit || '',
          ),
        );
      }
    } else if (orderType === TvOrderType.Stop) {
      if (
        Number(stopPrice) < Number(lowerCircuit) ||
        Number(stopPrice) > Number(upperCircuit)
      ) {
        throw new Error(
          LocaleService.translate(
            'CIRCUIT_LIMIT_ERROR',
            lowerCircuit || '',
            upperCircuit || '',
          ),
        );
      }
    } else if (orderType === TvOrderType.Limit) {
      if (
        Number(limitPrice) < Number(lowerCircuit) ||
        Number(limitPrice) > Number(upperCircuit)
      ) {
        throw new Error(
          LocaleService.translate(
            'CIRCUIT_LIMIT_ERROR',
            lowerCircuit || '',
            upperCircuit || '',
          ),
        );
      }
    }

    return true;
  };

  preOrderPlace = async (
    preOrder: OrderWithCustomFields,
    underSurveillance = false,
  ): Promise<PlaceOrderResult> => {
    const { segment, token } = decomposeExchangeToken(preOrder.symbol);

    const isSegmentEnabled = UserStore.isSegmentEnabled(segment);

    if (!isSegmentEnabled) return Promise.reject(new Error('Segment is not enabled'));

    const isUserDormantForSegment = UserStore.isUserDormantForSegment(segment);

    if (isUserDormantForSegment) return Promise.reject(new Error(LocaleService.translate('DORMANT_USER_ERROR')));

    const isOrderIsValid = await this.checkIfOrderIsValid(preOrder);

    if (!isOrderIsValid) return Promise.reject(new Error('Order is not valid'));

    const {
      productType,
      side,
    } = OrderingServiceFormatter.generateDataForCheckEdis(preOrder);

    const isEdisNeeded = ValidationService.isEdisRequired(
      segment,
      productType,
      side,
    );

    if (!isEdisNeeded) {
      return this.placeOrder(preOrder, underSurveillance);
    }

    if (isEdisNeeded) {
      const scrips = await getScripforEdis(
        segment,
        token,
        Number(preOrder.qty),
        0);
      const isEdisSuccess = await edisService.authoriseEdis(scrips);

      if (isEdisSuccess) {
        return this.placeOrder(preOrder, underSurveillance);
      }

      return { orderId: '' };
    }

    return Promise.reject(new Error('Order placement failed'));
  };

  modifyOrder = async (order: OrderWithCustomFields) => {
    const isOrderIsValid = await this.checkIfOrderIsValid(order);

    if (!isOrderIsValid) return;

    const data = OrderingServiceFormatter.generateModifyPlaceOrderData(order);

    if (data) {
      const scripData = await SearchService.searchByExchangeToken(order.symbol);
      const response = await OrderingServiceFetch.modifyOrderRequest(data);

      const orderStatus = response?.data?.responseDetailList?.[0]?.status;
        if (orderStatus === 'R') {
          if (broker.instance) {
            broker.instance.showNotification(
              LocaleService.translate('MODIFY_ORDER'),
              response?.data?.responseDetailList?.[0]?.message || 'Order placement failed',
              NotificationType.Error,
            );
        }
        return Promise.reject(new Error('Order placement failed'));
      }

      if (response?.success) {
        const orderData = data?.orders[0];
        OrderingServiceFormatter.sendMixpanelEventForCommonOrderPlaced(
          'Modified',
          orderData,
          scripData,
          this.getInstantOrderEnabled(),
        );
      }
    }
  };

  cancelOrder = async (orderId: string) => {
    const order = OrdersTVService.findOrderById(orderId);

    if (order) {
      const orderCategory = OrdersTVService.getOrderCategory(order?.product);
      const response = await OrderingServiceFetch.cancelOrderRequest([{
        omsId: String(order?.orderNumber?.oms),
        upstoxId: order?.orderNumber?.orderRefId,
        productType: order?.product,
        instrumentKey: order.instrument_key,
        amo: !!order?.isAMO,
        tsl: !!order?.orderNumber?.orderRefId?.includes('TSL'),
        orderCategory,
      }]);

      if (!response.success) {
        if (broker.instance) {
          broker.instance.showNotification(
            LocaleService.translate('CANCEL_ORDER'),
            response?.error?.message,
            NotificationType.Error,
          );
        }
        const eventData = {
          'Order Status': 'Cancelled',
          Exchange: order?.instrument?.e,
          Quantity:
            (Number.isNaN(order.quantity?.traded)
              ? 0
              : Number(order.quantity?.traded)) +
            Number(order.quantity?.pending),
          'Product Type': order.product,
          Side: order.side,
          'Trigger Price': order?.price?.trigger,
          'Average Price': order?.price?.avg,
          'Order type': order.product,
          'Disclosed Qty': order.quantity?.disclosed,
          Token: order?.instrument?.t,
          isInstantOrder: this.getInstantOrderEnabled() ? 'Yes' : 'No',
        };
        CleverTapService.sendEvent(EVENTS.ORDER_CANCELLED, eventData);
      }
    }
  };

  private getInstantOrderEnabled(): boolean {
    return broker.instance?.isInstantOrderEnabled?.() ?? false;
  }
}

export default new OrderingService();
